import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslationService } from '@ng-omar/translation';
import {
    GanttItem,
    GanttConfig,
    GanttTemplateContext,
    TaskPosition,
    GanttDateService,
} from '../services';

interface GanttRowLabelParams {
    name: string;
    progress: string;
    from: string;
    to: string;
}

// Matches the `p-1.5` padding applied to nested rows, so indentation adds to it
// instead of replacing it. Bind `p-1.5` with ngClass; [class.p-1.5] does not parse.
const nestedBasePadding = 6;
const indentationStep = 16;

@Component({
    selector: 'app-gantt-task-row',
    // cspell:disable
    template: `
        <div
            class="flex w-full border-b border-slate-100 transition-colors duration-200 hover:bg-slate-50"
            [ngClass]="{
                'border-b-2 border-primary-200':
                    isExpanded && hasChildren && config.allowExpansion,
                'bg-slate-50': isPlanTaskRowBackground,
                'bg-white': depth > 0,
            }"
            role="row"
            [attr.aria-expanded]="hasChildren ? isExpanded : null"
            [attr.aria-label]="rowLabelKey | translate: labelParams"
        >
            <div
                class="flex w-full min-w-[200px] items-center justify-between border-e border-slate-200 sm:w-1/3 sm:min-w-[300px]"
                [ngClass]="{ 'p-2': depth === 0, 'p-1.5': depth > 0 }"
                [style.min-height.px]="
                    depth > 0 ? config.subtaskHeight : config.taskHeight
                "
                [style.padding-inline-start.px]="indentationPaddingStart"
            >
                <div class="flex w-full items-center justify-between gap-2">
                    <div class="flex min-w-0 flex-1 items-center gap-2">
                        @if (showDisclosureToggle) {
                            <button
                                class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                                [attr.aria-label]="
                                    toggleLabelKey | translate: labelParams
                                "
                                (click)="onToggleTask()"
                                type="button"
                            >
                                <i
                                    class="fa-light transition-transform duration-300"
                                    [ngClass]="{
                                        'fa-chevron-down': showChevronDown,
                                        'fa-chevron-right': showChevronRight,
                                        'fa-chevron-left': showChevronLeft,
                                    }"
                                    aria-hidden="true"
                                ></i>
                            </button>
                        }
                        <!-- Reserve the h-6 toggle slot so leaf labels cannot outdent parents. -->
                        @if (showDisclosureSpacer) {
                            <span
                                class="h-6 w-6 flex-shrink-0"
                                aria-hidden="true"
                            ></span>
                        }
                        @if (
                            (depth === 0 && taskTemplate) ||
                            (depth > 0 && subtaskTemplate)
                        ) {
                            <ng-container
                                *ngTemplateOutlet="
                                    depth === 0
                                        ? taskTemplate
                                        : subtaskTemplate;
                                    context: { $implicit: item }
                                "
                            ></ng-container>
                        } @else {
                            <button
                                class="min-w-0 flex-1 rounded-lg text-start transition-colors hover:bg-slate-100 hover:text-primary-600 focus:text-primary-600 focus:outline-none"
                                [ngClass]="
                                    depth === 0
                                        ? 'p-1.5 text-sm font-medium text-slate-700 sm:text-[15px]'
                                        : 'p-1 text-xs text-slate-600 sm:text-sm'
                                "
                                [appTooltip]="item | ganttItemTooltip"
                                style="
                                    white-space: normal;
                                    word-wrap: break-word;
                                    overflow-wrap: break-word;
                                    line-height: 1.3;
                                "
                                [attr.aria-label]="
                                    nameLabelKey | translate: labelParams
                                "
                                (click)="onTaskClick()"
                            >
                                {{ item.name }}
                            </button>
                        }
                    </div>

                    @if (config.showProgress && item.progress !== undefined) {
                        <span
                            role="img"
                            class="mt-1 hidden flex-shrink-0 self-start rounded-full px-2 py-0.5 text-xs font-medium sm:flex"
                            [ngClass]="
                                depth > 0
                                    ? config.subtaskColors.progressBadge
                                    : config.taskColors.progressBadge
                            "
                            [attr.aria-label]="
                                progressLabelKey | translate: labelParams
                            "
                        >
                            {{ progressDisplay }}%
                        </span>
                    }
                </div>
            </div>

            <div
                class="relative flex-1"
                [style.min-height.px]="
                    depth > 0 ? config.subtaskHeight : config.taskHeight
                "
            >
                <div
                    class="grid h-full w-full"
                    [style.grid-template-columns]="
                        'repeat(' + displayDatesLength + ', 1fr)'
                    "
                ></div>
                <div
                    class="absolute left-0 top-0 h-full px-1"
                    style="width: 100%"
                >
                    <div
                        class="absolute top-1/2 rounded-full transition-all duration-200 hover:shadow-md"
                        [ngClass]="
                            depth > 0
                                ? [
                                      config.subtaskColors.background,
                                      '-mt-2 h-3 sm:h-4',
                                  ]
                                : [
                                      config.taskColors.background,
                                      '-mt-2.5 h-4 sm:h-5',
                                  ]
                        "
                        [ngStyle]="taskPosition"
                        [appTooltip]="item | ganttItemTooltip"
                        [attr.aria-label]="
                            timelineLabelKey | translate: labelParams
                        "
                        role="progressbar"
                        [attr.aria-valuenow]="progressValue"
                        [attr.aria-valuemin]="0"
                        [attr.aria-valuemax]="100"
                    >
                        @if (
                            config.showProgress && item.progress !== undefined
                        ) {
                            <div
                                class="h-full rounded-full transition-all duration-300"
                                [ngClass]="
                                    depth > 0
                                        ? config.subtaskColors.progress
                                        : config.taskColors.progress
                                "
                                [style.width]="item.progress * 100 + '%'"
                            ></div>
                        }
                    </div>
                </div>
            </div>
        </div>
    `,
    // cspell:enable
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class GanttTaskRowComponent<T = unknown>
    implements OnInit, OnChanges, OnDestroy
{
    @Input() public item!: GanttItem<T>;
    @Input() public config!: GanttConfig;
    @Input() public taskPosition!: TaskPosition;
    @Input() public depth = 0;
    @Input() public isExpanded = false;
    @Input() public hasChildren = false;
    @Input() public displayDatesLength = 0;
    @Input() public taskTemplate?: TemplateRef<GanttTemplateContext<T>>;
    @Input() public subtaskTemplate?: TemplateRef<GanttTemplateContext<T>>;

    @Output() public taskClick = new EventEmitter<GanttItem<T>>();
    @Output() public toggleTask = new EventEmitter<string>();

    public isPlanTaskRowBackground = false;
    public progressDisplay = '0';
    public progressValue = 0;
    public showChevronDown = false;
    public showChevronRight = false;
    public showChevronLeft = false;
    public showDisclosureToggle = false;
    public showDisclosureSpacer = false;
    public indentationPaddingStart: number | null = null;
    public labelParams: GanttRowLabelParams = {
        name: '',
        progress: '0',
        from: '',
        to: '',
    };
    public rowLabelKey = 'translate_gantt_task_name';
    public nameLabelKey = 'translate_gantt_task_name';
    public toggleLabelKey = 'translate_gantt_expand_item';
    public progressLabelKey = 'translate_gantt_progress_percent_complete';
    public timelineLabelKey = 'translate_gantt_timeline';

    private readonly unsubscribeAll = new Subject<void>();

    public constructor(
        private readonly translationService: TranslationService,
        private readonly dateService: GanttDateService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    public ngOnInit(): void {
        this.translationService.translateService.onLangChange
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(() => {
                this.updateDisplayState();
                this.cdr.markForCheck();
            });
    }

    public ngOnChanges(_changes: SimpleChanges): void {
        this.updateDisplayState();
    }

    public ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    public onTaskClick(): void {
        this.taskClick.emit(this.item);
    }

    public onToggleTask(): void {
        this.toggleTask.emit(this.item.id);
    }

    private updateDisplayState(): void {
        const isRtl = !!this.translationService.currentLanguage?.isRtl;
        const progressPercent =
            this.item.progress !== undefined
                ? Math.round(this.item.progress * 100)
                : null;

        this.isPlanTaskRowBackground =
            this.depth === 0 && this.isPlanTaskType();
        this.progressDisplay =
            progressPercent !== null ? progressPercent.toString() : '0';
        this.progressValue = progressPercent ?? 0;
        this.showChevronDown = this.isExpanded;
        this.showChevronRight = !this.isExpanded && !isRtl;
        this.showChevronLeft = !this.isExpanded && isRtl;
        this.showDisclosureToggle =
            this.hasChildren && !!this.config?.allowExpansion;
        this.showDisclosureSpacer =
            !this.hasChildren && !!this.config?.allowExpansion;
        this.indentationPaddingStart =
            this.depth > 0
                ? nestedBasePadding + this.depth * indentationStep
                : null;

        this.updateLabels(progressPercent);
    }

    private updateLabels(progressPercent: number | null): void {
        const from = this.item.from
            ? this.dateService.formatTooltipDate(this.item.from)
            : '';
        const to = this.item.to
            ? this.dateService.formatTooltipDate(this.item.to)
            : '';

        this.labelParams = {
            name: this.item.name ?? '',
            progress: this.progressDisplay,
            from,
            to,
        };

        const isNested = this.depth > 0;

        this.nameLabelKey = isNested
            ? 'translate_gantt_subtask_name'
            : 'translate_gantt_task_name';

        if (progressPercent === null) {
            this.rowLabelKey = this.nameLabelKey;
        } else {
            this.rowLabelKey = isNested
                ? 'translate_gantt_subtask_name_progress'
                : 'translate_gantt_task_name_progress';
        }

        this.toggleLabelKey = this.isExpanded
            ? 'translate_gantt_collapse_item'
            : 'translate_gantt_expand_item';
        this.timelineLabelKey =
            from && to
                ? 'translate_gantt_timeline_from_to'
                : 'translate_gantt_timeline';
    }

    private isPlanTaskType(): boolean {
        return (
            this.hasType(this.item.data) ||
            this.hasType(
                (this.item as GanttItem<T> & { originalData?: unknown })
                    .originalData
            )
        );
    }

    private hasType(value: unknown): boolean {
        return (
            typeof value === 'object' &&
            value !== null &&
            (value as { type?: string }).type === 'task'
        );
    }
}
